/**
 * Word-level prose diff. Ported from the #5 prototype (question 2).
 *
 * The counterpoint to @pierre/diffs: no hunks, no line numbers, no syntax
 * highlighting. The rubric reads as one continuous piece of prose with insertions and
 * deletions inline, so a reworded clause reads as a reworded clause rather than as a
 * replaced line. Best on the small clause edits that dominate this corpus.
 */
import { Box } from '@mui/material';
import { diffWordsWithSpace } from 'diff';
import { useMemo } from 'react';

interface Props {
  oldText: string;
  newText: string;
  /** 'inline' = one merged stream. 'columns' = two panes, each highlighting its own side. */
  mode: 'inline' | 'columns';
}

const proseSx = {
  fontFamily: '"SF Mono", Menlo, monospace',
  fontSize: 13,
  lineHeight: 1.65,
  whiteSpace: 'pre-wrap' as const,
  wordBreak: 'break-word' as const,
};

const addedSx = { bgcolor: '#d1f0d7', color: '#04260c', borderRadius: '2px' };
const removedSx = {
  bgcolor: '#ffd7d5',
  color: '#3a0a08',
  borderRadius: '2px',
  textDecoration: 'line-through',
  textDecorationColor: 'rgba(0,0,0,0.35)',
};

export const WordDiff = ({ oldText, newText, mode }: Props) => {
  const parts = useMemo(() => diffWordsWithSpace(oldText, newText), [oldText, newText]);

  if (mode === 'inline') {
    return (
      <Box sx={proseSx}>
        {parts.map((part, i) => (
          <Box
            key={i}
            component="span"
            sx={part.added ? addedSx : part.removed ? removedSx : undefined}
          >
            {part.value}
          </Box>
        ))}
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
      <Box sx={proseSx}>
        {parts
          .filter(p => !p.added)
          .map((part, i) => (
            <Box key={i} component="span" sx={part.removed ? removedSx : undefined}>
              {part.value}
            </Box>
          ))}
      </Box>
      <Box sx={{ ...proseSx, borderLeft: '1px solid', borderColor: 'divider', pl: 2 }}>
        {parts
          .filter(p => !p.removed)
          .map((part, i) => (
            <Box key={i} component="span" sx={part.added ? addedSx : undefined}>
              {part.value}
            </Box>
          ))}
      </Box>
    </Box>
  );
};

/** Rough "how much actually changed" figure, for the header stats. */
export const changeStats = (oldText: string, newText: string) => {
  const parts = diffWordsWithSpace(oldText, newText);
  let added = 0;
  let removed = 0;
  let unchanged = 0;
  for (const p of parts) {
    const words = (p.value.match(/[^\s]+/g) ?? []).length;
    if (p.added) added += words;
    else if (p.removed) removed += words;
    else unchanged += words;
  }
  return { added, removed, unchanged, total: added + removed + unchanged };
};
